import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { DIAGNOSTIC } from "@/constants/testIds";
import { postIntake } from "@/lib/intakeApi";
import { useDiagnostic } from "@/features/diagnostic/DiagnosticContext";
import { Container, GhostButton, PrimaryButton } from "@/components/site/Primitives";
import { DiagnosticHeader, PreviewNotice, SubmittedContext } from "@/components/diagnostic/Chrome";
import { QuestionStep, ReviewStep, SentStep } from "@/components/diagnostic/Steps";

function EmptySession({ onHome }) {
  return (
    <div data-testid={DIAGNOSTIC.emptySession} className="py-24">
      <h1 className="font-display text-3xl font-light tracking-[-0.03em] text-fg">No business need in this session.</h1>
      <p className="mt-4 max-w-xl text-subtle">Drafts are kept in memory only, so they reset when the page is refreshed. Start again from the assessment.</p>
      <PrimaryButton className="mt-10" onClick={onHome}>Go to the assessment</PrimaryButton>
    </div>
  );
}

export default function Diagnostic() {
  const { state, reset, leadSent } = useDiagnostic();
  const navigate = useNavigate();
  const [phase, setPhase] = useState(state.lead?.sent ? "sent" : "question");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");

  const save = async (final) => {
    setPending(true);
    setError("");
    const res = await postIntake(`/leads/${state.lead.id}/follow-up`, { token: state.lead.token, answers: { "successful-outcome": state.answers["successful-outcome"] || "" }, final });
    setPending(false);
    if (!res.ok) return setError(res.message);
    if (final) { leadSent(); setPhase("sent"); } else setPhase("review");
  };

  const view = state.submitted ? phase : "empty";
  if (state.submitted && !state.lead?.id) return <Navigate to="/assessment/contact" replace />;
  const goEdit = () => navigate("/");
  const startOver = () => { reset(); navigate("/"); };

  return (
    <div data-testid={DIAGNOSTIC.page} className="theme-dark fa-diagnostic relative min-h-screen overflow-hidden bg-canvas text-fg">
      <div aria-hidden className="fa-ambient fa-ambient--diag" />
      <DiagnosticHeader onExit={goEdit} />
      <main className="relative">
        <Container className="pb-24 pt-8">
          <PreviewNotice />
          {view === "empty" && <EmptySession onHome={goEdit} />}
          {view !== "empty" && (
            <div className="mt-12 grid gap-12 lg:grid-cols-[320px_1fr] lg:gap-20">
              <SubmittedContext submitted={state.submitted} onEdit={goEdit} />
              <div className="min-w-0">
                {view === "question" && <QuestionStep onContinue={() => save(false)} pending={pending} error={error} />}
                {view === "review" && <ReviewStep onEditAnswer={() => { setError(""); setPhase("question"); }} onSend={() => save(true)} pending={pending} error={error} />}
                {view === "sent" && <SentStep onHome={startOver} onStartOver={startOver} />}
              </div>
            </div>
          )}
          {view === "question" && (
            <GhostButton className="mt-16" onClick={goEdit}>
              <ArrowLeft size={16} aria-hidden /> Back to my business need
            </GhostButton>
          )}
        </Container>
      </main>
    </div>
  );
}
